import React, { type ReactNode, useEffect, useState } from 'react';
import type { ProductTableHeaders } from '@hub/common/ProductTable/ProductTableHeader';
import { ProductTableHeader, ProductTableSortingState } from '@hub/common/ProductTable/ProductTableHeader';
import Image from 'next/image';
import { v4 } from 'uuid';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { getEventCategory, TRACKED_STANDARD_EVENT_ACTIONS } from '@hub/libs/tracking';
import { useRouter } from 'next/router';
import type { UnitSystem } from '@igus/kopla-domain';
import type { CurrentLifeTimeConfiguration } from '@hub/free-form-hub/common/lifetime/useLifetime';
import { LifeTimePopUpInfo } from '@hub/free-form-hub/common/lifetime/LifeTimePopUpInfo';

export type ProductTableProps = {
  headers: ProductTableHeaders[];
  tableData: TableData;
  currentLifeTimeConfiguration: CurrentLifeTimeConfiguration;
  defaultSorting?: {
    key: string;
    sortingState: ProductTableSortingState;
  };
  onRowClick?: (row: TableRow) => void;
  unitSystem: UnitSystem;
};

export type TableCell = {
  key: string;
  value?: string | number;
  type?: TableCellTypes;
  styles?: string;
  children?: TableCell[];
  template?: ReactNode;
};

export type TableRow = {
  id: string;
  cells: TableCell[];
};
export type TableData = TableRow[];

export const tableCellTypes = {
  IMAGE: 'image',
  CHILDREN: 'children',
};

export type TableCellTypes = (typeof tableCellTypes)[keyof typeof tableCellTypes];

export const ProductTable = ({
  headers,
  tableData,
  defaultSorting,
  currentLifeTimeConfiguration,
  onRowClick,
  unitSystem,
}: ProductTableProps) => {
  const router = useRouter();
  const [activeLifeTimePopUp, setActiveLifeTimePopUp] = useState(false);
  const [tableHeaders, setTableHeaders] = useState(headers);
  const [currentTableData, setCurrentTableData] = useState(tableData);
  const { CustomEvent } = usePiwikPro();

  const getNewSortingState = (sortingStateArg: ProductTableSortingState) => {
    switch (sortingStateArg) {
      case ProductTableSortingState.Ascending:
        return ProductTableSortingState.Descending;
      case ProductTableSortingState.NoSort:
        return ProductTableSortingState.Ascending;
      default:
        return ProductTableSortingState.NoSort;
    }
  };

  const updateHeaderSortingState = (key: string, sortingState: ProductTableSortingState) => {
    const updatedTableHeaders = tableHeaders.map(header => {
      const isHeaderMatch = header.key === key;
      const updatedSubHeaders = header.subHeaders?.map(subHeader => {
        const isSubHeaderMatch = subHeader.key === key;
        return {
          ...subHeader,
          sortingState: isSubHeaderMatch ? getNewSortingState(sortingState) : ProductTableSortingState.NoSort,
        };
      });

      return {
        ...header,
        sortingState: isHeaderMatch ? getNewSortingState(sortingState) : ProductTableSortingState.NoSort,
        subHeaders: updatedSubHeaders,
      };
    });

    setTableHeaders(updatedTableHeaders);
  };

  const getSortedData = (
    dataToSort: TableData,
    key: string,
    currentSortingHeaderState: ProductTableSortingState
  ): TableData => {
    const findValue = (row: TableCell[]) => {
      const tableCell = row.find(
        cell => cell.key === key || (cell.children && cell.children.find(child => child.key === key))
      );
      return tableCell?.children ? tableCell.children.find(child => child.key === key)?.value : tableCell?.value;
    };

    return [...dataToSort].sort((a, b) => {
      const valuesA = findValue(a.cells);
      const valuesB = findValue(b.cells);

      if (valuesA === -1) {
        return 1;
      }
      if (valuesB === -1) {
        return -1;
      }

      if (currentSortingHeaderState === ProductTableSortingState.Descending) {
        if (typeof valuesB === 'string') {
          return valuesB.localeCompare(valuesA as string);
        }
        return valuesB - (valuesA as number);
      }

      if (typeof valuesA === 'string') {
        return valuesA.localeCompare(valuesB as string);
      }
      return valuesA - (valuesB as number);
    });
  };

  const onSort = (key: string, sortingState: ProductTableSortingState) => {
    updateHeaderSortingState(key, sortingState);
    setCurrentTableData(getSortedData(currentTableData, key, getNewSortingState(sortingState)));
    const eventCategory = getEventCategory(router.asPath);
    if (eventCategory) {
      CustomEvent.trackEvent(eventCategory, TRACKED_STANDARD_EVENT_ACTIONS.articleSort, key);
    }
  };

  const renderCell = (cell: TableCell) => {
    if (cell.children) {
      return (
        <td className={cell.styles} key={cell.key}>
          <div className="flex mr-8">
            {cell.children.map(children => (
              <div className={`w-full ${children.styles || ''}`} key={children.key}>
                {children.value}
              </div>
            ))}
          </div>
        </td>
      );
    }
    return (
      <td className={cell.styles || ''} key={cell.key} data-testid={`product-table-cell-${cell.key}`}>
        {cell.template && cell.template}
        {cell.type === tableCellTypes.IMAGE ? (
          <div className="flex justify-center">
            <Image src={cell.value.toString()} alt="img" width={48} height={48} />
          </div>
        ) : (
          !cell.template && cell.value
        )}
      </td>
    );
  };

  useEffect(() => {
    const sortTableOnInit = defaultSorting?.key && tableHeaders.length > 0;

    if (sortTableOnInit) {
      setCurrentTableData(getSortedData(tableData, defaultSorting.key, defaultSorting.sortingState));
    } else {
      setCurrentTableData(tableData);
    }
  }, [tableData]);

  return (
    <>
      <table className="w-full" data-testid="product-table">
        <ProductTableHeader headers={tableHeaders} sort={onSort} openInfoModal={() => setActiveLifeTimePopUp(true)} />
        <tbody className="text-center" data-testid="product-table-body">
          {currentTableData.map(row => {
            const uuId = v4();

            return (
              <tr
                className="border border-[#AFAFAF] text-sm relative hover:bg-[#FDF2E5] transition duration-200 cursor-pointer text-left select-none"
                key={uuId}
                onClick={() => onRowClick(row)}
              >
                {row.cells.map(cell => renderCell(cell))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <LifeTimePopUpInfo
        currentLifeTimeConfiguration={currentLifeTimeConfiguration}
        activeModal={activeLifeTimePopUp}
        setActiveModal={value => setActiveLifeTimePopUp(value)}
        unitSystem={unitSystem}
      />
    </>
  );
};
